import { HTMLProps } from 'react';

import {
  LinearProgress,
  Theme,
  Typography,
  WithStyles,
  createStyles,
  withStyles,
} from '@material-ui/core';

import classNames from 'classnames';

import { useMuiDialogContext } from '../DialogCompositeMui/context';
import { useMultiStepDialogContext } from '../MultiStepDialog/context';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      position: 'relative',
      width: '100%',
      paddingTop: theme.spacing(8),
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      flexShrink: 0,
    },
    rootFullScreen: {
      maxWidth: 700,
      margin: '0 auto',
    },
    progressBar: {
      // make the bar slightly wider than the rest of the content to make it look more aligned
      left: -3,
      right: 3,
      width: 'calc(100% + 6px)',
      marginBottom: theme.spacing(2),
    },
  });

export type ProgressProps = WithStyles<typeof styles> &
  HTMLProps<HTMLDivElement>;

const Progress = (props: ProgressProps) => {
  const { classes, className, ...rest } = props;
  const { activeStep, steps } = useMultiStepDialogContext();
  const { props: dialogProps } = useMuiDialogContext();
  const isFullScreen = Boolean(dialogProps?.fullScreen);

  // No progress bar if there are no steps yet
  if (steps.length === 0) {
    return null;
  }

  // Ensure activeStep is within bounds
  const safeActiveStep = Math.max(0, Math.min(activeStep, steps.length - 1));
  const currentStep = steps[safeActiveStep];
  const totalSteps = steps.length;

  // Handle single step case to avoid division by zero
  const progressPercentage =
    totalSteps === 1 ? 100 : (safeActiveStep / (totalSteps - 1)) * 100;

  return (
    <div
      className={classNames(classes.root, className, {
        [classes.rootFullScreen]: isFullScreen,
      })}
      {...rest}
    >
      <LinearProgress
        variant="determinate"
        value={progressPercentage}
        className={classes.progressBar}
      />
      <Typography variant="body2" color="textSecondary">
        Step {activeStep + 1} of {totalSteps}{' '}
        {currentStep?.optional && (
          <Typography variant="inherit" color="textSecondary" component="span">
            (optional)
          </Typography>
        )}
      </Typography>
    </div>
  );
};

Progress.displayName = 'MultiStepDialog.Progress';

export default withStyles(styles)(Progress);
